import type { WidgetEventName, WidgetEventPayload } from "@ii-gp-widget/shared/browser";
import type { Locale } from "./i18n";

export type Track = (event: WidgetEventName, detail?: string) => void;

export interface TrackerContext {
  apiOrigin: string;
  slug: string;
  locale: Locale;
}

export function buildEventPayload(
  sessionId: string,
  locale: Locale,
  event: WidgetEventName,
  detail?: string,
): WidgetEventPayload {
  return { event, detail: detail ?? null, sessionId, lang: locale };
}

/**
 * Usage stats (views, tab switches, clicks) for POST /premises/:slug/events — what the old
 * version read out of its access logs. Fire-and-forget: sendBeacon with a string body goes out
 * as text/plain, a CORS-safelisted type, so there's no preflight and it survives the visitor
 * navigating away (e.g. a chain or footer link click). Never throws into the widget.
 */
export function createTracker(context: TrackerContext): Track {
  const sessionId = crypto.randomUUID(); // one per widget load, groups this visit's events
  const url = new URL(`/premises/${encodeURIComponent(context.slug)}/events`, context.apiOrigin).toString();

  return (event, detail) => {
    const body = JSON.stringify(buildEventPayload(sessionId, context.locale, event, detail));
    try {
      if (navigator.sendBeacon?.(url, body)) return;
      // No sendBeacon, or its queue was full: same request, same text/plain body.
      void fetch(url, { method: "POST", body, keepalive: true }).catch(() => {});
    } catch {
      // Stats are best-effort; nothing a visitor should ever see.
    }
  };
}
